import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NoteIcon from '@mui/icons-material/Note';
import PersonIcon from '@mui/icons-material/Person';
import type { IncidentTimelineEntry, IncidentStatus } from '../../types/canvision.types';

interface IncidentTimelineProps {
  timeline?: IncidentTimelineEntry[];
}

// Format time for display
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// Get icon for action
function getActionIcon(action: IncidentTimelineEntry['action'], toStatus?: IncidentStatus | null) {
  if (action === 'note_added') {
    return <NoteIcon fontSize="small" />;
  }
  if (action === 'assigned') {
    return <PersonIcon fontSize="small" />;
  }
  if (action === 'dispatched') {
    return <LocalShippingIcon fontSize="small" color="secondary" />;
  }

  // Status changes
  switch (toStatus) {
    case 'dispatched':
      return <LocalShippingIcon fontSize="small" color="secondary" />;
    case 'dismissed':
      return <CheckCircleIcon fontSize="small" color="success" />;
    default:
      return <FiberManualRecordIcon fontSize="small" color="primary" />;
  }
}

// Get description for action
function getActionDescription(entry: IncidentTimelineEntry): string {
  const user = entry.user_name || 'System';

  switch (entry.action) {
    case 'created':
      return `Incident created${entry.details ? ` (${entry.details})` : ''}`;
    case 'status_changed':
      return `${user} changed status to ${entry.to_status?.toUpperCase()}`;
    case 'note_added':
      return `${user} added a note`;
    case 'dispatched':
      return `${user} dispatched guard`;
    case 'assigned':
      return `${user} assigned to incident`;
    default:
      return entry.details || 'Unknown action';
  }
}

function IncidentTimeline({ timeline = [] }: IncidentTimelineProps) {
  if (timeline.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No timeline events
        </Typography>
      </Box>
    );
  }

  // Sort by created_at descending (newest first)
  const sortedTimeline = [...timeline].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <List dense disablePadding>
      {sortedTimeline.map((entry, index) => (
        <ListItem
          key={entry.id}
          sx={{
            py: 0.5,
            px: 0,
            alignItems: 'flex-start',
            borderLeft: index < sortedTimeline.length - 1 ? '2px solid' : 'none',
            borderColor: 'divider',
            ml: 1.5,
            pl: 2,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 32,
              mt: 0.5,
              ml: -2.5,
              bgcolor: 'background.paper',
            }}
          >
            {getActionIcon(entry.action, entry.to_status)}
          </ListItemIcon>
          <ListItemText
            primary={getActionDescription(entry)}
            secondary={`${formatDate(entry.created_at)} ${formatTime(entry.created_at)}`}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: entry.action === 'created' ? 'medium' : 'normal',
            }}
            secondaryTypographyProps={{
              variant: 'caption',
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

export default IncidentTimeline;
